import React, { useState, useEffect } from 'react';
import { Card, Button, Descriptions, Space, Typography, Empty, Modal, Input, Image, Alert } from 'antd';
import { LockOutlined, SafetyOutlined } from '@ant-design/icons';
import { useSnackbar } from 'notistack';
import * as Sentry from "@sentry/react";
import { useTranslation } from 'react-i18next';
import ChangePasswordModal from '../components/ChangePasswordModal';
import PageLayout from '../components/PageLayout';
import { usePageLayout } from '../hooks/usePageLayout';
import { getTOTPStatus, setupTOTP, enableTOTP, disableTOTP } from '../apiCalls';
import { getStoredUser } from '../utils/safeLocalStorage';

const AccountSettings = () => {
    const { t } = useTranslation();
    const [user, setUser] = useState(null);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [totpEnabled, setTotpEnabled] = useState(false);
    const [showSetupModal, setShowSetupModal] = useState(false);
    const [showDisableModal, setShowDisableModal] = useState(false);
    const [qrCode, setQrCode] = useState(null);
    const [totpCode, setTotpCode] = useState('');
    const [disablePassword, setDisablePassword] = useState('');
    const [totpLoading, setTotpLoading] = useState(false);
    const pageLayoutProps = usePageLayout();
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        setUser(getStoredUser());
        getTOTPStatus().then(data => setTotpEnabled(data.totpEnabled)).catch(() => { });
    }, []);

    const handleSetup2FA = async () => {
        setTotpLoading(true);
        try {
            const data = await setupTOTP();
            setQrCode(data.qrCode);
            setShowSetupModal(true);
        } catch (err) {
            enqueueSnackbar(t('settings.error.setup2fa'), { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setTotpLoading(false);
        }
    };

    const handleEnable2FA = async () => {
        if (!totpCode || totpCode.length !== 6) {
            enqueueSnackbar(t('settings.error.enterCode'), { variant: 'warning' });
            return;
        }
        setTotpLoading(true);
        try {
            await enableTOTP(totpCode);
            setTotpEnabled(true);
            setShowSetupModal(false);
            setTotpCode('');
            setQrCode(null);
            enqueueSnackbar(t('settings.success.twoFactorEnabled'), { variant: 'success' });
        } catch (err) {
            enqueueSnackbar(err.response?.data || t('settings.error.invalidCode'), { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setTotpLoading(false);
        }
    };

    const handleDisable2FA = async () => {
        if (!disablePassword) {
            enqueueSnackbar(t('settings.error.enterPassword'), { variant: 'warning' });
            return;
        }
        setTotpLoading(true);
        try {
            await disableTOTP(disablePassword);
            setTotpEnabled(false);
            setShowDisableModal(false);
            setDisablePassword('');
            enqueueSnackbar(t('settings.success.twoFactorDisabled'), { variant: 'success' });
        } catch (err) {
            enqueueSnackbar(err.response?.data || t('settings.error.disable2fa'), { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setTotpLoading(false);
        }
    };

    if (!user) {
        return <Empty description={t('settings.notFound')} style={{ marginTop: 50 }} />;
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'Never';
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    };

    return (
        <PageLayout {...pageLayoutProps}>
            <div style={{ maxWidth: '900px', margin: '0 auto' }}>
                <Typography.Title level={2}>{t('settings.title')}</Typography.Title>

                <Card style={{ marginBottom: 24 }}>
                    <Typography.Title level={3}>{t('settings.profile')}</Typography.Title>
                    <Descriptions column={1} bordered>
                        <Descriptions.Item label={t('settings.firstName')}>
                            {user.firstName}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('settings.lastName')}>
                            {user.lastName}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('settings.email')}>
                            {user.email}
                        </Descriptions.Item>
                        <Descriptions.Item label={t('settings.role')}>
                            <span style={{
                                padding: '4px 8px',
                                background: '#e6f4ff',
                                color: '#1890ff',
                                borderRadius: 4,
                                textTransform: 'capitalize'
                            }}>
                                {user.role}
                            </span>
                        </Descriptions.Item>
                        <Descriptions.Item label={t('settings.accountCreated')}>
                            {formatDate(user.createdAt)}
                        </Descriptions.Item>
                    </Descriptions>
                </Card>

                <Card style={{ marginBottom: 24 }}>
                    <Typography.Title level={3}>{t('settings.security')}</Typography.Title>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <div>
                            <Typography.Text strong>{t('settings.password')}</Typography.Text>
                            <Typography.Paragraph type="secondary">
                                {user.lastPasswordChange
                                    ? t('settings.lastChanged', { date: formatDate(user.lastPasswordChange) })
                                    : t('settings.noPasswordChange')
                                }
                            </Typography.Paragraph>
                        </div>
                        <Button
                            type="primary"
                            icon={<LockOutlined />}
                            onClick={() => setShowPasswordModal(true)}
                        >
                            {t('settings.changePassword')}
                        </Button>

                        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                            <Typography.Text strong>{t('settings.twoFactor')}</Typography.Text>
                            <Typography.Paragraph type="secondary">
                                {totpEnabled
                                    ? t('settings.twoFactorEnabled')
                                    : t('settings.twoFactorDisabled')
                                }
                            </Typography.Paragraph>
                            {totpEnabled ? (
                                <Space>
                                    <Alert message={t('settings.twoFactorAlert')} type="success" showIcon style={{ padding: '4px 12px' }} />
                                    <Button danger onClick={() => setShowDisableModal(true)}>{t('settings.disable2fa')}</Button>
                                </Space>
                            ) : (
                                <Button
                                    icon={<SafetyOutlined />}
                                    onClick={handleSetup2FA}
                                    loading={totpLoading}
                                >
                                    {t('settings.enable2fa')}
                                </Button>
                            )}
                        </div>
                    </Space>
                </Card>

                {/* 2FA Setup Modal */}
                <Modal
                    title={t('settings.setup2fa.title')}
                    open={showSetupModal}
                    onCancel={() => { setShowSetupModal(false); setTotpCode(''); setQrCode(null); }}
                    footer={null}
                    centered
                >
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Typography.Text>
                            {t('settings.setup2fa.step1')}
                        </Typography.Text>
                        <Typography.Text>
                            {t('settings.setup2fa.step2')}
                        </Typography.Text>
                        {qrCode && (
                            <div style={{ textAlign: 'center', margin: '16px 0' }}>
                                <Image src={qrCode} alt={t('settings.setup2fa.step2')} preview={false} width={200} />
                            </div>
                        )}
                        <Typography.Text>
                            {t('settings.setup2fa.step3')}
                        </Typography.Text>
                        <Input
                            placeholder="000000"
                            maxLength={6}
                            value={totpCode}
                            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                            onPressEnter={handleEnable2FA}
                            style={{ fontSize: 20, letterSpacing: 6, textAlign: 'center' }}
                        />
                        <Button type="primary" block loading={totpLoading} onClick={handleEnable2FA}>
                            {t('settings.setup2fa.confirm')}
                        </Button>
                    </Space>
                </Modal>

                {/* 2FA Disable Modal */}
                <Modal
                    title={t('settings.disable2faModal.title')}
                    open={showDisableModal}
                    onCancel={() => { setShowDisableModal(false); setDisablePassword(''); }}
                    footer={null}
                    centered
                >
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Alert
                            message={t('settings.twoFactorAlertDesc')}
                            type="warning"
                            showIcon
                        />
                        <Typography.Text>{t('settings.disable2faModal.instruction')}</Typography.Text>
                        <Input.Password
                            placeholder={t('settings.passwordCurrentPlaceholder')}
                            value={disablePassword}
                            onChange={(e) => setDisablePassword(e.target.value)}
                            onPressEnter={handleDisable2FA}
                        />
                        <Button danger block loading={totpLoading} onClick={handleDisable2FA}>
                            {t('settings.disable2faModal.disable')}
                        </Button>
                    </Space>
                </Modal>

                <Card>
                    <Typography.Title level={3}>{t('settings.accountInfo')}</Typography.Title>
                    <Descriptions column={1} size="small">
                        <Descriptions.Item label={t('settings.properties')}>
                            {user.properties && user.properties.length > 0
                                ? user.properties.join(', ')
                                : t('settings.noPropertiesAssigned')
                            }
                        </Descriptions.Item>
                        <Descriptions.Item label={t('settings.createdBy')}>
                            {user.createdBy?.email || t('settings.system')}
                        </Descriptions.Item>
                    </Descriptions>
                </Card>

                <ChangePasswordModal
                    visible={showPasswordModal}
                    onClose={() => setShowPasswordModal(false)}
                    email={user.email}
                />
            </div>
        </PageLayout>
    );
};

export default AccountSettings;
